import { z } from 'zod';
import { Controller, useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { CxCard, CxTabButton, CxWrap, Line } from '../../components/Sx';
import FormInputField from '../../../common/components/formElements/FormInputField';
import { KeyIcon, EyeIcon, EyeOffIcon } from '@heroicons/react/outline';
import { useEffect, useState } from 'react';
import { PublicProfileS } from '../../services/ProfilePublic';
import DatePicker from 'react-datepicker';
import { parseISO } from 'date-fns';
import Loader from '../../components/Loader';
import InputMask from 'comigo-tech-react-input-mask/lib/react-input-mask.development';
import CustomSuccessModal from '../../../common/components/customSuccessModal/CustomSuccessModal';
import defaultImage from '../../../images/profile.png';
import { useInfoStore } from '../../../App';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
// import { EyeIcon, EyeOffIcon } from '@heroicons/react/outline';



const date = new Date();
const MIN_AGE = 18;
const MAX_DATE = new Date(date.setFullYear(date.getFullYear() - MIN_AGE)).toJSON().slice(0, 10);

const Password = {
  REGEX: /^(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]+$/,
  MESSAGE: 'Password must contain at least one uppercase letter, one number, and one symbol',
  MIN: 'Password must be at least 8 characters long'
};

const Document = {
  REGEX: /^\d{8}-\d{1}$/,
  MESSAGE: 'Incorrect format'
};
const MAX_FILE_SIZE = 5000000;
const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];


const formSchema = z
  .object({
    personalImage: z
      .any()
      .refine(
        (file) => {
          if (typeof file === 'string') return true; // skip validation for image string
          return file?.size < MAX_FILE_SIZE;
        },
        'Max image size is 5MB.'
      )
      .refine(
        (file) => {
          if (typeof file === 'string') return true;
          return ACCEPTED_IMAGE_TYPES.includes(file?.type);
        },
        'Only .jpg, .jpeg, .png and .webp formats are supported.'
      ),

    firstName: z.string(),
    lastName: z.string(),
    conademNumber: z.string().optional(),
    countryCode: z.string().optional(),
    phone: z.string().optional(),
    fullPhone: z.string().optional(),
    address: z
      .array(
        z.object({
          address: z.string().min(1, 'Address is required'),
        })
      )
      .min(1, 'At least one address is required'),


    documentType: z.string(),
    personalDocument: z.string(),
    birthDate: z.date(),
    email: z.string(),
    oldPassword: z.string().min(8, { message: Password.MIN }).regex(Password.REGEX, { message: Password.MESSAGE }).optional().or(z.literal('')),
    newPassword: z.string().min(8, { message: Password.MIN }).regex(Password.REGEX, { message: Password.MESSAGE }).optional().or(z.literal('')),
    confPassword: z.string().min(8, { message: Password.MIN }).regex(Password.REGEX, { message: Password.MESSAGE }).optional().or(z.literal(''))
  })
  .refine((data) => data.newPassword === data.confPassword, { path: ['confPassword'], message: 'Passwords do not match' });
// .refine((data) => data.documentType === 'dui' ? Document.REGEX.test(data.personalDocument) : true, { path: ['personalDocument'], message: Document.MESSAGE });

const APP_SERVER = process.env.REACT_APP_API_SERVER;
const ErrMsg = ({ message }) => (message ? <p className="mt-2 text-sm text-red-700">{message}</p> : null);

function ProfilePublic() {
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfPassword, setShowConfPassword] = useState(false);

  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successBody, setSuccessBody] = useState('');

  const [previewImage, setPreviewImage] = useState('');

  const [isLoading, setIsLoading] = useState(true);
  const [refresh, setRefresh] = useState(false);
  const { register, handleSubmit, formState, getValues, reset, control, setValue, trigger } = useForm({ resolver: zodResolver(formSchema) });
  const { errors } = formState;
  const { fields, append, remove, update } = useFieldArray({
    control,
    name: 'address',
  });

  const [docTypes, setDocTypes] = useState([]);
  const [documentType, setDocumentType] = useState('');

  const { userInfo, setUserInfo } = useInfoStore((state) => state);

  const _createSuccessMessage = (message) => {
    setShowSuccessModal(true);
    setSuccessBody(message);
  };

  const onCloseSuccessMessage = () => {
    setShowSuccessModal(false);
    setSuccessBody('');
  };

  const saveForm = async (values) => {
    const { oldPassword, newPassword, personalImage, fullPhone } = values;
    let updatePassword = false;
    if (oldPassword && newPassword) {
      updatePassword = true;
    }

    const formData = new FormData();

    for (const key of Object.keys(values)) {
      if (key === "address") {
        formData.append("address", JSON.stringify(values[key]));
      } else if (key === "birthDate") {
        formData.append("birthDate", new Date(values[key]).toISOString());
      } else if (key === "personalImage") {
        if (typeof values[key] === "string") {
          //  important 
          const response = await fetch(`${APP_SERVER}/${values[key]}`);
          const blob = await response.blob();
          const fileName = values[key].split('/').pop();
          const file = new File([blob], fileName, { type: blob.type });
          formData.append("personalImage", file);
        } else {
          formData.append("personalImage", values[key]);
        }
      } else if (key === "fullPhone") {
        // Skip fullPhone as it's just for UI, we send countryCode and phone separately
        continue;
      } else {
        formData.append(key, values[key]);
      }
    }

    formData.append("updatePassword", JSON.stringify(updatePassword));

    PublicProfileS.updateProfile(formData)
      .then(() => _createSuccessMessage('Horario actualizado para el perfil.'))
      .then(() => setRefresh(!refresh))
      .catch((err) => console.log(err));
  };


  useEffect(() => {
    PublicProfileS.documentTypes().then((res) => {
      let arr = res.data.map((item) => ({ id: item.id, label: item.documentName }));
      setDocTypes(arr);
    });
    setIsLoading(true);
    PublicProfileS.getProfile(userInfo.id)
      .then((res) => {


        reset({
          firstName: res.data.firstName ?? '',
          lastName: res.data.lastName ?? '',
          countryCode: res.data.countryCode ?? '',
          phone: res.data.phone ?? '',
          fullPhone: res.data.fullPhone ?? `${res.data.countryCode || ''}${res.data.phone || ''}`,
          address: res?.data?.address,
          conademNumber: res.data.conademNumber ?? '',
          documentType: res.data.documentType ?? '',
          personalDocument: res.data.personalDocument ?? '',
          birthDate: parseISO(res.data.birthDate) ?? '',
          email: res.data.email ?? '',
          personalImage: res.data.personalImage ?? ''
        });
        // setPreviewImage(process.env.REACT_APP_MEDIA_SERVER + '/' + res.data.personalImage);
        const img = res.data.personalImage ? `${APP_SERVER}/${res.data.personalImage}` : defaultImage;
        setPreviewImage(img);
        setDocumentType(res.data.documentType);
      })
      .then(() => setIsLoading(false));

  }, [refresh]);


  const handleImageChange = (e) => {
    const file = e.target.files[0];
    setValue('personalImage', file);
    trigger('personalImage', { shouldValidate: true });
    setPreviewImage(URL.createObjectURL(file));
  };

  if (isLoading) return <Loader />;

  return (
    <div className="space-y-6 sm:px-6 lg:px-0 lg:col-span-10 side-bar-right bg-dl-screen-lightblue ">
      <div className="">
        <div className="profile-top-content">
          <h4>Bienvenido Doctor</h4>
          <p>Ingresa la siguiente información para con tu perfil personal, tus pacientes NO podrán ver esta información.</p>
        </div>
      </div>
      <form onSubmit={handleSubmit(saveForm)}>
        <CxWrap>
          <CxCard>Modificar la foto de perfil</CxCard>
          <Line />
          <CxCard>
            <div className="flex flex-col items-center">
              <div className="mb-4">
                <img src={previewImage} alt="Preview" className="mt-4 max-w-full h-32 w-32 rounded-full" />
              </div>
              <div className="rounded-full border border-gray-100 bg-white p-3 shadow-md w-1/3">
                <label htmlFor="upload" className="flex flex-col items-center gap-2 cursor-pointer">
                  <span className="text-gray-600 font-medium">Importar foto de perfil</span>
                </label>
                <input id="upload" type="file" accept="image/*" onChange={handleImageChange} className="hidden" />
              </div>
              <ErrMsg message={errors.personalImage?.message} />
            </div>
          </CxCard>
        </CxWrap>

        <CxWrap>
          <CxCard>Datos generaless</CxCard>
          <Line />
          <CxCard>
            <div className="grid grid-cols-2 gap-4 ">
              <div className='mb-3'>
                <FormInputField identifier="firstName" label="Nombres" type="string" registration={register('firstName')} errorMessage={errors.firstName?.message} />
              </div>
              <div>
                <FormInputField identifier="lastName" label="Apellidos" type="string" registration={register('lastName')} errorMessage={errors.lastName?.message} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <FormInputField
                  identifier="conademNumber"
                  label="Número de autorización CONADEM"
                  type="string"
                  registration={register('conademNumber')}
                  errorMessage={errors.conademNumber?.message}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Teléfono</label>
                <Controller
                  name="fullPhone"
                  control={control}
                  render={({ field: { onChange, value, ref } }) => {
                    const mergedValue = value || `${getValues('countryCode') || ''}${getValues('phone') || ''}`;
                    return (
                      <PhoneInput
                        value={mergedValue}
                        onChange={(val, country) => {
                          const codeVal = `+${country.dialCode}`;
                          const phoneVal = val.replace(country.dialCode, '').replace(/^\+/, '');
                          setValue('countryCode', codeVal);
                          setValue('phone', phoneVal);
                          onChange(val);
                        }}
                        enableSearch
                        disableDropdown={false}
                        disableCountryCode={false}
                        disableAreaCodes={true}
                        countryCodeEditable={false}
                        placeholder="Ingrese su número"
                        containerClass="rounded-full w-full custom-phone-container1"
                        inputClass="w-full h-10 pl-14 pr-4 rounded-full border border-gray-300 focus:outline-none focus:ring-0 focus:border-gray-300 active:outline-none active:ring-0 active:border-gray-300"
                        buttonClass="rounded-l-full border-r border-gray-300 bg-white focus:outline-none focus:ring-0 focus:border-gray-300 active:outline-none active:ring-0 active:border-gray-300"
                        specialLabel=""
                        inputProps={{ ref }}
                      />
                    );
                  }}
                />
                {errors.fullPhone && (
                  <p className="text-red-700 font-light">{errors.fullPhone.message}</p>
                )}
              </div>
            </div>

          </CxCard>
        </CxWrap>
        <CxWrap>
          <CxCard>Documentación</CxCard>
          <Line />
          <CxCard>
            <div className="grid grid-cols-2 gap-4 ">
              <div className='mb-4'>
                <label htmlFor={'documentType'} className="block font-medium mb-2 text-sm text-black">
                  Tipo de documento de identidad
                </label>
                <select
                  {...register('documentType')}
                  onChange={(e) => {
                    setDocumentType(e.target.value);
                    reset({ personalDocument: '' });
                  }}
                  className="mt-1 block w-full bg-white border border-gray-300 rounded-full shadow-sm py-2 px-3 focus:outline-none focus:ring-dl-primary-500 focus:border-dl-primary-500 sm:text-sm font-normal	"
                >
                  {docTypes.map((item) => (
                    <option key={item.id} value={item.label}>
                      {item.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                {/* <FormInputField identifier="personalDocument" label="Número de documento" type="string" registration={register('personalDocument')} errorMessage={errors.personalDocument?.message} /> */}
                <label className="block text-sm font-medium text-gray-700 mb-2">Numero de documento</label>
                <InputMask
                  identifier="personalDocument"
                  type="text"
                  name="personalDocument"
                  id="personalDocument"
                  autoComplete="family-name"
                  label="Numero de documento"
                  className="mt-1 block w-full border border-gray-300 rounded-full shadow-sm py-2 px-3 focus:outline-none focus:ring-dl-primary-700 focus:border-dl-primary-700 sm:text-sm"
                  mask={documentType === 'DUI' ? '9999999-9' : ''}
                  {...register('personalDocument')}
                />
                {errors.personalDocument?.message ? <p className="mt-2 text-sm text-red-700">{errors.personalDocument?.message}</p> : null}
              </div>
            </div>
            <div className="grid grid-cols-6 gap-4">

              <div className="col-start-1 col-end-7 ">
                <label className="block font-medium mb-2 text-sm text-black">Fecha de nacimiento</label>
                <Controller
                  control={control}
                  name="birthDate"
                  render={({ field }) => (
                    <DatePicker
                      selected={field.value}
                      value={field.value}
                      dateFormat="dd/MM/yyyy"
                      onChange={(date) => field.onChange(date)}
                      maxDate={new Date(MAX_DATE)}
                      peekNextMonth
                      showMonthDropdown
                      showYearDropdown
                      dropdownMode="select"
                      placeholderText="DD/MM/AAAA"
                      className="mt-1 block w-full border border-gray-300 rounded-full shadow-sm py-2 px-3 focus:outline-none focus:ring-dl-primary-700 focus:border-dl-primary-700 sm:text-sm"
                    />
                  )}
                />
              </div>
            </div>
          </CxCard>
        </CxWrap>
        <CxWrap>
          <CxCard>Cuenta</CxCard>
          <Line />
          <CxCard>
            <div className="grid grid-cols-6 gap-4">
              <div className="col-start-1 col-end-7 ">
                <FormInputField identifier="email" label="Correo" type="string" registration={register('email')} errorMessage={errors.email?.message} disabled={true} />
              </div>
              <div className="col-start-1 col-end-4 relative">
                <FormInputField
                  type={showOldPassword ? 'text' : 'password'}
                  iconComponent={<KeyIcon className="h-5 w-5 text-gray-400" />}
                  identifier="oldPassword"
                  label="Contraseña"
                  registration={register('oldPassword')}
                  errorMessage={errors.oldPassword?.message}
                />
                <span
                  className="absolute right-4 top-9 cursor-pointer"
                  onClick={() => setShowOldPassword((prev) => !prev)}
                >
                  {showOldPassword ? <EyeOffIcon className="h-5 w-5 text-gray-500" /> : <EyeIcon className="h-5 w-5 text-gray-500" />}
                </span>
              </div>

              <div className="col-start-1 col-end-4 relative">
                <FormInputField
                  type={showNewPassword ? 'text' : 'password'}
                  iconComponent={<KeyIcon className="h-5 w-5 text-gray-400" />}
                  identifier="newPassword"
                  label="Nueva contraseña"
                  registration={register('newPassword')}
                  errorMessage={errors.newPassword?.message}
                />
                <span
                  className="absolute right-4 top-9 cursor-pointer"
                  onClick={() => setShowNewPassword((prev) => !prev)}
                >
                  {showNewPassword ? <EyeOffIcon className="h-5 w-5 text-gray-500" /> : <EyeIcon className="h-5 w-5 text-gray-500" />}
                </span>
              </div>

              <div className="col-start-1 col-end-4 relative">
                <FormInputField
                  type={showConfPassword ? 'text' : 'password'}
                  iconComponent={<KeyIcon className="h-5 w-5 text-gray-400" />}
                  identifier="confPassword"
                  label="Confirmar contraseña"
                  registration={register('confPassword')}
                  errorMessage={errors.confPassword?.message}
                />
                <span
                  className="absolute right-4 top-9 cursor-pointer"
                  onClick={() => setShowConfPassword((prev) => !prev)}
                >
                  {showConfPassword ? <EyeOffIcon className="h-5 w-5 text-gray-500" /> : <EyeIcon className="h-5 w-5 text-gray-500" />}
                </span>
              </div>

            </div>
          </CxCard>
        </CxWrap>
        <div className="px-4 py-3  text-right sm:px-6">
          <CxTabButton label="Actualizar" />
        </div>
      </form>
      {showSuccessModal && <CustomSuccessModal title={'Acción ejecutada con éxito!'} bodyText={successBody} buttonText={'Cerrar'} buttonAction={onCloseSuccessMessage} open={showSuccessModal} onClose={onCloseSuccessMessage} />}
    </div>
  );
}

export default ProfilePublic;
